// STEP LIVE-CONSOLE-OVERLAY-COLLAPSE-PIN-1 · Console Overlay 제어 박스 UI preference.
//
// 7개 Overlay 제어 박스 (위치 · QR · 표시형태 · 사이즈표 · 재고 · NOTICE · 타이머)
// 의 접힘/고정 상태를 판매자 계정 단위로 persistence.  Overlay runtime state
// (announce / display / qr / notice / timer backend) 와 완전 분리됨 — 이 훅은
// localStorage 전용 UI preference 저장소.
//
// 저장 구조:
//   livemoa:seller-live-control:overlay-panels:{seller_id}
//     = { [panelKey]: { collapsed: bool, pinned: bool } }
//
// Normalize rules (hydrate + 변경 시 전부 적용):
//   · pinned=true 이면 collapsed=false 로 강제 (user 지시 §7).
//
// 기본값:
//   · 기존 preference 가 없으면 전부 { collapsed:false, pinned:false } (지시 §8).
//
// 신규 backend endpoint / collection 추가 금지 (지시 §6).
import { useCallback, useEffect, useRef, useState } from 'react';

export const OVERLAY_PANEL_KEYS = Object.freeze([
  'position',
  'qr',
  'preset',
  'size-table',
  'inventory',
  'notice',
  'timer',
]);

const KEY_PREFIX = 'livemoa:seller-live-control:overlay-panels';

const _storageKey = (sellerId) => `${KEY_PREFIX}:${sellerId || 'anon'}`;

const _emptyPrefs = () => {
  const base = {};
  for (const k of OVERLAY_PANEL_KEYS) {
    base[k] = { collapsed: false, pinned: false };
  }
  return base;
};

const _normalize = (entry) => {
  const pinned = !!(entry && entry.pinned);
  const collapsed = pinned ? false : !!(entry && entry.collapsed);
  return { collapsed, pinned };
};

const _hydrate = (raw) => {
  const base = _emptyPrefs();
  if (!raw) return base;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== 'object') return base;
    for (const k of OVERLAY_PANEL_KEYS) {
      if (parsed[k] && typeof parsed[k] === 'object') {
        base[k] = _normalize(parsed[k]);
      }
    }
  } catch { /* ignore malformed localStorage */ }
  return base;
};

export default function useOverlayPanelsPreference(sellerId) {
  const [prefs, setPrefs] = useState(() => {
    if (typeof window === 'undefined') return _emptyPrefs();
    try {
      const raw = window.localStorage.getItem(_storageKey(sellerId));
      return _hydrate(raw);
    } catch {
      return _emptyPrefs();
    }
  });

  // seller 가 바뀌면 그 seller 의 preference 를 다시 hydrate (다른 판매자 섞임 방지).
  const sellerRef = useRef(sellerId);
  useEffect(() => {
    if (sellerRef.current === sellerId) return;
    sellerRef.current = sellerId;
    try {
      const raw = window.localStorage.getItem(_storageKey(sellerId));
      setPrefs(_hydrate(raw));
    } catch {
      setPrefs(_emptyPrefs());
    }
  }, [sellerId]);

  const persist = useCallback((next) => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(_storageKey(sellerId), JSON.stringify(next));
    } catch { /* storage quota / disabled */ }
  }, [sellerId]);

  const toggleCollapsed = useCallback((panelKey) => {
    setPrefs((prev) => {
      const entry = prev[panelKey] || { collapsed: false, pinned: false };
      // 지시 §5 · 고정 ON 이면 접기 불가.
      if (entry.pinned) return prev;
      const nextEntry = _normalize({ ...entry, collapsed: !entry.collapsed });
      const next = { ...prev, [panelKey]: nextEntry };
      persist(next);
      return next;
    });
  }, [persist]);

  const setPinned = useCallback((panelKey, pinned) => {
    setPrefs((prev) => {
      const entry = prev[panelKey] || { collapsed: false, pinned: false };
      const nextEntry = _normalize({ pinned: !!pinned, collapsed: entry.collapsed });
      const next = { ...prev, [panelKey]: nextEntry };
      persist(next);
      return next;
    });
  }, [persist]);

  const getPanel = useCallback((panelKey) => (
    prefs[panelKey] || { collapsed: false, pinned: false }
  ), [prefs]);

  return { prefs, getPanel, toggleCollapsed, setPinned };
}
